<template>
  <div>
    <PageHeader />
    <ProjInfo :projectInfo="projectInfo" :changelog="changelog" />


    <v-container class="section-block">
        <v-row>
            <v-col class="col-12 col-md-4 col-sm-6">
                <h2>Walkthrough</h2>
                <h4>Basics</h4>
                <ol>
                    <li>Create a <b>new marker</b></li>
                    <li>Link <b>selected keyframes</b> to an event marker</li>
                    <li>Drag event markers around the timeline</li>
                    <li>Click <b>Update timing</b> to retime all the linked elements</li>
                </ol>
                <h4>Nested keys</h4>
                <ol>
                    <li>A precomp's markers will be visible as layer markers</li>
                    <li>Click <b>Link Layer Marker</b> to select the and link to the parent comp marker</li>
                    <li>Click <b>Update timing</b> to retime all the nested keyframes</li>
                </ol>
            </v-col>
            <v-col class="col-12 col-md-8 col-sm-6">
                <video width="100%" :src="video.tutorial" :poster="images.poster" controls />
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-12 col-sm-6 col-md-4">
                    <div class=" group-header">Usage</div>
                </v-col>
            <v-col class="col-12 col-md-8"> </v-col>
            <v-col class="col-12 col-sm-6 col-md-4">
                <img :src="images.createEvent" width="100%" alt="">
                <h2>Create an event</h2>
                <p>
                    Events are just markers. <i>Windfish</i> provides a interface to create them easily.
                </p>
                <ul>
                    <li>Enter an event name</li>
                    <li>Click to create a <b>New Marker</b></li>
                </ul>
            </v-col>
            <v-col class="col-12 col-sm-6 col-md-4">
                <img :src="images.linkKeys" width="100%" alt="">
                <h2>Link selected keyframes</h2>
                <ul>
                    <li>Select keyframes</li>
                    <li>Click the <b>Link Selected keyframes</b> button</li>
                    <li>A popup will ask which event marker to link the keyframes to</li>
                    <li>Expressions will be created</li>
                </ul>

                <p><b>Shift click</b> to remove selected keyframes from their event</p> 
            </v-col>
            <v-col class="col-12 col-sm-6 col-md-4">
                <img :src="images.linkMarker" width="100%" alt="">
                <h2>Link selected layer marker</h2>
                <ul>
                    <li>Select a layer</li>
                    <li>Click the <b>Link layer marker</b> button</li>
                    <li>A popup will ask which event marker and which layer marker to link (this is because you cannot directly select a markers in Ae)</li>
                    <li>The layer marker witll turn blue</li>
                    <li>Nested keys will be updated once you click <b>Update timing</b></li>
                </ul>

                <p><b>Shift click</b> to unlink all markers from the selected layer</p> 
            </v-col>
        </v-row>


        <v-row>
            <v-col class="col-12 col-sm-6 col-md-4">
                <div class=" group-header">Gestures</div>
            </v-col>
            <v-col class="col-12 col-md-8"> </v-col>
            <v-col class="col-12 col-md-8">
                <p>Streamline the creation of UI gestures with these 2 onscreen elements. Smears are dynamic and their usage is simple.</p>
            </v-col>
            <v-col class="col-12 col-md-4"/>
            <v-col class="col-12 col-lg-4 col-md-4 col-sm-6">
                <img :src="images.tap" width="100%" alt="">
                <h3>Tap</h3>
            </v-col>
            <v-col class="col-12 col-lg-4 col-md-4 col-sm-6">
                <img :src="images.swipe" width="100%" alt="">
                <h3>Swipe</h3>
            </v-col>
        </v-row>

    </v-container>

    <!-- <InstallInfo id="install" :projectInfo="projectInfo"/> -->
    <InstallInfo id="install" name="Windfish" scriptUI :hosts="['After Effects']"/>

    <Changelog :changelog="changelog" /> 
    
  </div>
</template>

<script>
// @ is an alias to /src

export default {
    name: 'Windfish',
    components: {

    },
    data: () => ({
        images: {
            poster: require('../assets/images/WindfishPoster.jpg'),
            createEvent: require('../assets/images/Windfish-CreateEvent.jpg'),
            linkKeys: require('../assets/images/Windfish-LinkKeys.jpg'),
            linkMarker: require('../assets/images/Windfish-LinkMarker.jpg'),
            tap: require('../assets/gif/Windfish-tap.gif'),
            swipe: require('../assets/gif/Windfish-swipe.gif'),
        },
        video: {
            tutorial: require('../assets/video/Windfish.mp4'),
        },
        projectInfo: {
            name: 'Windfish',
            subtitle: 'Keyframe and layer linking to events',
            source: true,
            about: `Drive an entire timeline with markers. Link keyframes within the current and nested comps to a couple comp markers and retime all linked elements with a click.
            
            The primary concept is that <b>keyframes</b> and <b>layer markers</b> may be linked to <b>comp markers</b>. If a layer marker is actually a precomped comp marker with nested keyframes linked to it, then updating in the parent comp will update the nested keys.
            
            The complexity of this nesting is up to you. 
            
            The power is yours.`
        },
        changelog: [
            {
                version: '0.1.6',
                date: 'Sept 13, 2021',
                changed: [
                    'Gestures do not trim the start',
                    'Remove depreciated comp name from expression',
                ],
                fixed: [
                    'Adding keys to a link'
                ],
            },
            {
                version: '0.1.5',
                date: 'Aug 17, 2021',
                changed: [
                    'Consolidate comp and nested comp update buttons',
                    'Cleanup UI with tabs',
                ],
            },
            {
                version: '0.1.4',
                date: 'June 29, 2021',
                changed: [
                    'Removed layer linking in favor of a more clear workflow of not retiming layers'
                ],
                fixed: [
                    'Retain nested marker names'
                ],
            },
            {
                version: '0.1.3',
                date: 'July 21, 2021',
                Added: [
                    'Reaching into a nested comp for marker data',
                    'Update nested comp marker times',
                    'Link layers to markers',
                ],
            },
            {
                version: '0.1.2',
                date: 'June 29, 2021',
                fixed: [
                    'If selected prop has no keys',
                    'Targeted comp when linking keyframes',
                    'The order of shifting keyframes with larger offset values'
                ],
            },
            {
                version: '0.1.1',
                date: 'June 28, 2021',
                added: [
                    'Keyframes and layers may be linked to event markers in other comps',
                ],
                fixed: [
                    'Selecting multiple layers',
                    'Selecting arbitrary number of keyframes',
                    'Better logic for adding and clearing event linking'
                ],
            },
            {
                version: '0.1.0',
                date: 'June 23, 2021',
                comment: ['Initial release'],
            },
        ]
    }),
}
</script>